import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { BoxArrowRight, CreditCard2Front, ExclamationTriangle, Lock } from 'react-bootstrap-icons';
import { useLocale } from '../../i18n/LocaleContext';
import { canManageBilling, fetchBilling, fetchBillingStatus, isPlatformAdmin } from '../../api/billing';
import { REQUIRE_VALID_CARD } from '../../config/embedFlags';
import { MockBadge } from '../embed/EmbedShared';
import MockCardForm from './MockCardForm';
import { PriceList } from './UsageCard';
import { deriveCardStatus, gateDecision, onBillingChange } from './gateRules';
import '../embed/Embed.scss';
import './Billing.scss';
import './CardGate.scss';

// The card gate: with REQUIRE_VALID_CARD on, a brand without a validated card
// sees this full page instead of the dashboard. Adding the card here IS the
// validation — the SetupIntent confirm (with 3-D Secure when the bank asks),
// which Stripe checks with the issuer. Only cardStatus 'valid' opens the
// portal. Rules: gateRules.js.

const StripeCardForm = lazy(() => import('./StripeCardForm'));

/// The gate state for the selected brand. The first check shows a loading
/// screen; later re-checks (Refresh, a card change on Számlázás) keep the
/// current screen until the answer is in.
export function useCardGate({ brand, user, embedded, enabled = REQUIRE_VALID_CARD }) {
  const active = enabled && !embedded && !!brand && !!user;
  const isAdmin = isPlatformAdmin(user);
  const canManage = !!brand && canManageBilling(brand, user?.id, isAdmin);
  const [state, setState] = useState({ brandId: null, status: null, data: null, mock: false, error: null, loading: active });
  const seq = useRef(0);

  const check = useCallback(() => {
    if (!active) return Promise.resolve();
    const mine = ++seq.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    const load = canManage
      ? fetchBilling(brand.id).then((res) => ({ status: deriveCardStatus(res.data), data: res.data, mock: res.mock }))
      : (brand.cardStatus
        ? Promise.resolve({ status: brand.cardStatus, data: null, mock: false })
        : fetchBillingStatus(brand.id).then((res) => ({ status: deriveCardStatus(res.data), data: null, mock: res.mock })));
    return load
      .then((r) => {
        if (mine === seq.current) setState({ brandId: brand.id, ...r, error: null, loading: false });
      })
      .catch((err) => {
        if (mine !== seq.current) return;
        // No status readable for this user (403): nothing to gate on — the
        // API decides what they may open. Anything else: show the error.
        if (err?.status === 403) setState({ brandId: brand.id, status: null, data: null, mock: false, error: null, loading: false });
        else setState((s) => ({ ...s, brandId: brand.id, error: err, loading: false }));
      });
  }, [active, canManage, brand?.id, brand?.cardStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    check();
  }, [check]);

  useEffect(() => {
    if (!active) return undefined;
    return onBillingChange((e) => {
      if (!e.detail?.brandId || e.detail.brandId === brand.id) check();
    });
  }, [active, brand?.id, check]); // eslint-disable-line react-hooks/exhaustive-deps

  const current = state.brandId === brand?.id;
  let mode;
  if (!active) mode = 'open';
  else if (!current) mode = 'loading';
  else if (state.error && state.status == null) mode = 'error';
  else mode = gateDecision({ enabled, embedded, hasBrand: !!brand, status: state.status, isAdmin });

  return { mode, status: state.status, data: state.data, mock: state.mock, canManage, recheck: check, checking: state.loading };
}

/// "Nincs érvényes kártya" over the dashboard, for a platform admin let in.
export function AdminCardBanner({ status }) {
  const { t } = useLocale();
  return (
    <div className="gate-banner" role="status" data-testid="admin-card-banner">
      <ExclamationTriangle size={14} aria-hidden="true" />
      <span>{t('gate.adminBanner')}</span>
      <span className="gate-banner__status">{t(`gate.status.${status}`)}</span>
    </div>
  );
}

export default function CardGatePage({ brand, gate, brands = [], onSelectBrand, onLogout, onValid }) {
  const { t } = useLocale();
  const [formKey, setFormKey] = useState(0);
  const [result, setResult] = useState(null); // an answer after a save that did not validate
  const [opened, setOpened] = useState(false);
  const status = gate.status || 'missing';
  const key = gate.data?.publishableKey;

  // A saved card: ask the API whether it is now valid; in → Statisztikák.
  async function afterSave() {
    setResult(null);
    const res = await fetchBilling(brand.id).catch(() => null);
    const next = res ? deriveCardStatus(res.data) : null;
    if (next === 'valid') {
      onValid();
      return;
    }
    setResult(next || 'error');
    setFormKey((k) => k + 1);
    gate.recheck();
  }

  let form = null;
  if (gate.canManage && opened) {
    const props = { brandId: brand.id, onSaved: afterSave, onCancel: () => setOpened(false) };
    if (key && !gate.mock) {
      form = (
        <Suspense fallback={<div className="bill-form bill-muted">{t('shell.loading')}</div>}>
          <StripeCardForm key={formKey} {...props} publishableKey={key} />
        </Suspense>
      );
    } else if (gate.mock) {
      form = <MockCardForm key={formKey} {...props} />;
    } else {
      form = <p className="bill-inline-error" role="alert">{t('billing.card.unavailable')}</p>;
    }
  }

  const currency = gate.data?.usage?.currency || 'HUF';
  const title = gate.canManage ? t(`gate.title.${status}`) : t('gate.memberTitle');

  return (
    <div className="gate" data-testid="card-gate" data-status={status}>
      <div className="gate__card">
        <div className="gate__brand">
          {brand.logo ? (
            <img className="gate__logo" src={brand.logo} alt="" />
          ) : (
            <span className="gate__logo gate__logo--ph" aria-hidden="true">{brand.name?.[0]?.toUpperCase() || 'B'}</span>
          )}
          <span className="gate__brand-name" translate="no">{brand.name}</span>
          <MockBadge show={gate.mock} />
        </div>

        <h1 className="gate__title">{title}</h1>

        {gate.canManage ? (
          <>
            <p className="gate__lead">{t(`gate.lead.${status}`)}</p>
            {gate.data?.paymentMethod && status !== 'valid' && (
              <p className="gate__current">
                <CreditCard2Front size={14} aria-hidden="true" />
                {t('gate.current', { last4: gate.data.paymentMethod.last4 })}
              </p>
            )}

            <div className="gate__prices">
              <div className="bill-prices__title">{t('billing.price.title')}</div>
              <PriceList currency={currency} />
            </div>

            {result && (
              <p className="bill-inline-error" role="alert" data-testid="gate-error">
                {t(result === 'error' ? 'gate.checkFailed' : `gate.notValid.${result}`)}
              </p>
            )}

            {opened ? (
              <div className="gate__form">{form}</div>
            ) : (
              <button type="button" className="btn-o btn-o--dark gate__cta" onClick={() => setOpened(true)} data-testid="gate-add">
                {t(status === 'missing' ? 'billing.card.add' : 'gate.newCard')}
              </button>
            )}
            <p className="gate__fine">{t('billing.card.lead')}</p>
          </>
        ) : (
          <p className="gate__lead gate__lead--member" data-testid="gate-member">
            <Lock size={14} aria-hidden="true" />
            {t('gate.member')}
          </p>
        )}

        <div className="gate__foot">
          {brands.length > 1 && (
            <label className="gate__switch">
              <span>{t('gate.otherBrand')}</span>
              <select className="emb-input" value={brand.id} onChange={(e) => onSelectBrand(e.target.value)} data-testid="gate-brand">
                {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </label>
          )}
          <button type="button" className="btn-o btn-o--ghost btn-o--sm gate__logout" onClick={onLogout}>
            <BoxArrowRight size={14} aria-hidden="true" />
            {t('shell.logout')}
          </button>
        </div>
      </div>
    </div>
  );
}

/// The first check, or an error reading the status.
export function CardGateWaiting({ error, onRetry, onLogout }) {
  const { t } = useLocale();
  if (!error) return <div className="dash-loading"><span>{t('shell.loading')}</span></div>;
  return (
    <div className="gate" data-testid="card-gate-error">
      <div className="gate__card">
        <h1 className="gate__title">{t('gate.errorTitle')}</h1>
        <p className="gate__lead">{t('gate.errorBody')}</p>
        <div className="gate__foot">
          <button type="button" className="btn-o btn-o--dark btn-o--sm" onClick={onRetry}>{t('gate.retry')}</button>
          <button type="button" className="btn-o btn-o--ghost btn-o--sm gate__logout" onClick={onLogout}>
            <BoxArrowRight size={14} aria-hidden="true" />
            {t('shell.logout')}
          </button>
        </div>
      </div>
    </div>
  );
}
